import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { useGetIntegrationCatalogQuery } from '../../store/integrationCatalog/integrationCatalog'
import { SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import {
  getIntegrationServiceIdsForCategory,
  IntegrationCategory,
  IntegrationServiceId,
  isContactCreateEnabledInConfig,
  isContactUpdateEnabledInConfig,
  isFullMeetingExportEnabledInConfig,
  isIntegrationDefaultHidden,
} from '../../common/integrations'
import { getAccountCRMIntegrationConfig, getEffectiveAccountCRMIntegrationConfig, getEffectiveAccountIntegrationConfig } from '../../models/Account'
import { type User } from '../../models/User'
import { useCallback } from 'react'

export const useIntegrations = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery({ accountId })
  const { data: integrationCatalog, isLoading: isIntegrationCatalogLoading } = useGetIntegrationCatalogQuery({ accountId })

  const getIntegrationCatalogEntry = useCallback(
    (serviceId: IntegrationServiceId) => integrationCatalog?.data.find((entry) => entry.serviceId === serviceId),
    [integrationCatalog],
  )

  const isIntegrationAllowed = useCallback(
    (integration: IntegrationServiceId): boolean => {
      const defaultAllowed = !isIntegrationDefaultHidden(integration)
      const restrictions = account?.data?.enterpriseConfig.integrationsConfig.restrictions
      if (restrictions && (restrictions.allowedIntegrations?.length || restrictions.deniedIntegrations?.length)) {
        if (restrictions.allowedIntegrations?.length) {
          return restrictions.allowedIntegrations.includes(integration) && !restrictions.deniedIntegrations.includes(integration)
        }
        if (restrictions.deniedIntegrations.includes(integration)) {
          return false
        }
        return defaultAllowed
      }
      return defaultAllowed
    },
    [account],
  )

  const supportsMeetingExport = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      return !!getIntegrationCatalogEntry(serviceId)?.config?.meetingExport
    },
    [getIntegrationCatalogEntry],
  )

  const canExportMeeting = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      const config = getEffectiveAccountCRMIntegrationConfig(account?.data, serviceId)
      return supportsMeetingExport(serviceId) && isFullMeetingExportEnabledInConfig(config)
    },
    [account, supportsMeetingExport],
  )

  const supportsMeetingSummaryExport = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      return !!getIntegrationCatalogEntry(serviceId)?.config?.meetingSummaryExport
    },
    [getIntegrationCatalogEntry],
  )

  const supportsTaskExport = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      return !!getIntegrationCatalogEntry(serviceId)?.config?.taskExport
    },
    [getIntegrationCatalogEntry],
  )

  const supportsDocumentSummaryExport = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      return !!getIntegrationCatalogEntry(serviceId)?.config?.documentSummaryExport
    },
    [getIntegrationCatalogEntry],
  )

  const supportsDocumentUpload = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      return !!getIntegrationCatalogEntry(serviceId)?.config?.documentUpload
    },
    [getIntegrationCatalogEntry],
  )

  const supportsContactCreate = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      return !!getIntegrationCatalogEntry(serviceId)?.config?.contactCreate
    },
    [getIntegrationCatalogEntry],
  )

  const supportsContactUpdate = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      return !!getIntegrationCatalogEntry(serviceId)?.config?.contactUpdate
    },
    [getIntegrationCatalogEntry],
  )

  const supportsContactLinking = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      return !!getIntegrationCatalogEntry(serviceId)?.config?.contactLinking
    },
    [getIntegrationCatalogEntry],
  )

  const supportsDocumentContactUpdate = useCallback(
    (serviceId: IntegrationServiceId): boolean => {
      return !!getIntegrationCatalogEntry(serviceId)?.config?.documentContactUpdate
    },
    [getIntegrationCatalogEntry],
  )

  const canCreateOrUpdateContact = useCallback(
    (serviceId: IntegrationServiceId, contact: User): boolean => {
      const config = getEffectiveAccountIntegrationConfig(account?.data, serviceId)
      const isLinked = contact.integrations?.some((integration) => integration.serviceId === serviceId) ?? false
      const updateAllowed = supportsContactUpdate(serviceId) && isContactUpdateEnabledInConfig(config)
      if (isLinked) {
        return updateAllowed
      }
      return supportsContactCreate(serviceId) && (isContactCreateEnabledInConfig(config) || updateAllowed)
    },
    [account, supportsContactCreate, supportsContactUpdate],
  )

  const isIntegrationSyncDisabled = useCallback(
    (integration: IntegrationServiceId): boolean => {
      return getAccountCRMIntegrationConfig(account?.data, integration)?.isSyncDisabled === true
    },
    [account],
  )

  const isIntegrationCategoryAllowed = useCallback(
    (integrationCategory: string): boolean => {
      return getIntegrationServiceIdsForCategory(integrationCategory as IntegrationCategory).some((serviceId) => isIntegrationAllowed(serviceId))
    },
    [isIntegrationAllowed],
  )
  const calendarIntegrationOptionsByIntegration: {
    [key in IntegrationServiceId]?: SelectOption
  } = {
    ...(isIntegrationAllowed(IntegrationServiceId.GOOGLE_CALENDAR) && {
      [IntegrationServiceId.GOOGLE_CALENDAR]: { id: IntegrationServiceId.GOOGLE_CALENDAR, label: 'Google Calendar' },
    }),
    ...(isIntegrationAllowed(IntegrationServiceId.OUTLOOK_CALENDAR) && {
      [IntegrationServiceId.OUTLOOK_CALENDAR]: { id: IntegrationServiceId.OUTLOOK_CALENDAR, label: 'Microsoft Outlook Calendar' },
    }),
    ...(isIntegrationAllowed(IntegrationServiceId.EXCHANGE_CALENDAR) && {
      [IntegrationServiceId.EXCHANGE_CALENDAR]: {
        id: IntegrationServiceId.EXCHANGE_CALENDAR,
        label: 'Exchange Calendar (On-Premise)',
      },
    }),
  }
  const calendarIntegrationOptions = Object.values(calendarIntegrationOptionsByIntegration)

  return {
    isIntegrationAllowed,
    supportsMeetingExport,
    canExportMeeting,
    supportsMeetingSummaryExport,
    supportsTaskExport,
    supportsDocumentSummaryExport,
    supportsDocumentUpload,
    supportsContactCreate,
    supportsContactUpdate,
    supportsContactLinking,
    supportsDocumentContactUpdate,
    canCreateOrUpdateContact,
    isIntegrationSyncDisabled,
    isIntegrationCategoryAllowed,
    isLoading: isAccountLoading || isIntegrationCatalogLoading,
    calendarIntegrationOptions,
    calendarIntegrationOptionsByIntegration,
  }
}
